import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Save, Loader2, Plus, Trash2, GripVertical, Sparkles, Wand2, RefreshCw, Check, Copy, ChevronDown, ChevronUp, BarChart2, AlertTriangle, Link2, AlertCircle } from 'lucide-react';
import { adminGetGuide, adminCreateGuide, adminUpdateGuide, adminGetCategories, aiGenerateContent, aiGenerateSuggestions, aiImproveSection, aiScoreContent, aiCheckDuplicates, aiSuggestLinks } from '../lib/api';
import OpsLayout from '../components/OpsLayout';
import RichTextEditor from '../components/RichTextEditor';

const SECTION_TYPES = [
  { value: 'text', label: 'Text Block' },
  { value: 'list', label: 'Bullet List' },
  { value: 'cta', label: 'Call to Action' },
  { value: 'comparison', label: 'Side-by-Side' },
];

function SectionEditor({ section, index, onChange, onRemove, pageTitle }) {
  const [open, setOpen] = useState(true);
  const [improving, setImproving] = useState(false);
  const update = (key, value) => onChange(index, { ...section, [key]: value });

  const handleImprove = async (instruction) => {
    setImproving(true);
    try {
      const r = await aiImproveSection({ section, page_title: pageTitle, instruction });
      if (r.data.success && r.data.section) onChange(index, r.data.section);
      else alert('Could not parse AI response.');
    } catch (e) { alert('Improve failed: ' + (e.response?.data?.detail || e.message)); }
    setImproving(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`section-editor-${index}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors text-left">
        <div className="flex items-center gap-3">
          <GripVertical className="w-4 h-4 text-slate-300" />
          <span className="font-mono text-xs text-slate-400">#{index + 1}</span>
          <Select value={section.type || 'text'} onValueChange={(v) => update('type', v)}>
            <SelectTrigger className="w-[130px] h-7 text-xs rounded-lg border-slate-200" onClick={e => e.stopPropagation()}><SelectValue /></SelectTrigger>
            <SelectContent>{SECTION_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
          <span className="text-xs text-slate-500 truncate max-w-[200px]">{section.heading || section.button || ''}</span>
        </div>
        <div className="flex items-center gap-1">
          {/* AI Improve dropdown */}
          <div className="relative" onClick={e => e.stopPropagation()}>
            <Button variant="ghost" size="sm" disabled={improving} className="h-7 px-2 text-xs gap-1 text-violet-600 hover:bg-violet-50"
              onClick={async (e) => { e.stopPropagation(); handleImprove('Make it more engaging, specific, and SEO-optimized'); }}>
              {improving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              <span className="hidden sm:inline">Improve</span>
            </Button>
          </div>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); onRemove(index); }}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
          {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>
      {open && (
        <div className="p-4 pt-0 space-y-3 border-t border-slate-100">
          {section.type === 'text' && (
            <>
              <Input placeholder="Section heading" className="text-sm rounded-lg h-9" value={section.heading || ''} onChange={e => update('heading', e.target.value)} />
              <RichTextEditor value={section.body || ''} onChange={val => update('body', val)} />
            </>
          )}
          {section.type === 'list' && (
            <>
              <Input placeholder="Section heading" className="text-sm rounded-lg h-9" value={section.heading || ''} onChange={e => update('heading', e.target.value)} />
              <Textarea placeholder="One item per line..." className="text-sm rounded-lg min-h-[120px]" value={(section.items || []).join('\n')} onChange={e => update('items', e.target.value.split('\n').filter(Boolean))} />
              <p className="text-[10px] text-slate-400">One bullet point per line</p>
            </>
          )}
          {section.type === 'cta' && (
            <>
              <Input placeholder="CTA heading" className="text-sm rounded-lg h-9" value={section.heading || ''} onChange={e => update('heading', e.target.value)} />
              <Input placeholder="Subtext" className="text-sm rounded-lg h-9" value={section.sub || ''} onChange={e => update('sub', e.target.value)} />
              <Input placeholder="Button label" className="text-sm rounded-lg h-9" value={section.button || ''} onChange={e => update('button', e.target.value)} />
            </>
          )}
          {section.type === 'comparison' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Input placeholder="Left label" className="text-sm rounded-lg h-9 mb-2" value={section.left?.label || ''} onChange={e => update('left', { ...section.left, label: e.target.value, points: section.left?.points || [] })} />
                <Textarea placeholder="Points (one per line)" className="text-sm rounded-lg min-h-[100px]" value={(section.left?.points || []).join('\n')} onChange={e => update('left', { ...section.left, points: e.target.value.split('\n').filter(Boolean) })} />
              </div>
              <div>
                <Input placeholder="Right label" className="text-sm rounded-lg h-9 mb-2" value={section.right?.label || ''} onChange={e => update('right', { ...section.right, label: e.target.value, points: section.right?.points || [] })} />
                <Textarea placeholder="Points (one per line)" className="text-sm rounded-lg min-h-[100px]" value={(section.right?.points || []).join('\n')} onChange={e => update('right', { ...section.right, points: e.target.value.split('\n').filter(Boolean) })} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── AI Assistant Panel ──────────────────────────────────────
function AIAssistant({ form, categories, onApply }) {
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState('professional');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [sugType, setSugType] = useState('titles');
  const [suggestions, setSuggestions] = useState([]);
  const [sugLoading, setSugLoading] = useState(false);
  const [score, setScore] = useState(null);
  const [scoring, setScoring] = useState(false);

  // Auto-score when sections change
  const runScore = async () => {
    if (!form.title && !form.sections?.length) return;
    setScoring(true);
    try {
      const r = await aiScoreContent({ title: form.title, meta: form.meta, sections: form.sections || [] });
      setScore(r.data);
    } catch { }
    setScoring(false);
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    setResult(null);
    try {
      const r = await aiGenerateContent({
        prompt: prompt.trim(),
        page_title: form.title,
        page_category: form.category,
        tone,
        content_type: form.sections?.length > 0 ? 'improve' : 'full_page',
        existing_content: form.sections?.length > 0 ? JSON.stringify(form.sections.slice(0, 3)) : '',
      });
      if (r.data.success && r.data.content) {
        setResult(r.data.content);
      } else if (r.data.raw) {
        setResult({ raw: r.data.raw });
      }
    } catch (e) {
      alert('AI generation failed: ' + (e.response?.data?.detail || e.message));
    }
    setGenerating(false);
  };

  const handleSuggestions = async () => {
    setSugLoading(true);
    setSuggestions([]);
    try {
      const r = await aiGenerateSuggestions({
        type: sugType,
        topic: form.title || prompt || 'Burlington real estate',
        category: form.category || '',
      });
      if (r.data.success) setSuggestions(r.data.suggestions || []);
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
    }
    setSugLoading(false);
  };

  const applyGenerated = () => {
    if (!result || result.raw) return;
    onApply(result);
    setResult(null);
    setPrompt('');
  };

  return (
    <div className="space-y-4">
      {/* Content Quality Score */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <BarChart2 className="w-4 h-4 text-slate-400" />
            <p className="text-sm font-semibold text-slate-800">Content Score</p>
          </div>
          <Button onClick={runScore} disabled={scoring} variant="ghost" size="sm" className="h-7 text-xs gap-1 text-slate-500">
            {scoring ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Analyze
          </Button>
        </div>
        {score ? (
          <div>
            {/* Score ring */}
            <div className="flex items-center gap-4 mb-4">
              <div className="relative w-16 h-16">
                <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                  <circle cx="32" cy="32" r="28" fill="none" stroke="#f1f5f9" strokeWidth="5" />
                  <circle cx="32" cy="32" r="28" fill="none" stroke={score.seo_score >= 70 ? '#10b981' : score.seo_score >= 40 ? '#f59e0b' : '#ef4444'} strokeWidth="5"
                    strokeDasharray={`${(score.seo_score / 100) * 176} 176`} strokeLinecap="round" />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center font-mono text-lg font-bold text-slate-900">{score.seo_score}</span>
              </div>
              <div className="flex-1 space-y-1.5">
                <div className="flex justify-between text-[11px]">
                  <span className="text-slate-500">Words</span>
                  <span className={`font-mono font-medium ${score.word_count >= 800 ? 'text-emerald-600' : 'text-amber-600'}`}>{score.word_count}</span>
                </div>
                <div className="flex justify-between text-[11px]">
                  <span className="text-slate-500">Readability</span>
                  <span className={`font-mono font-medium ${score.readability >= 70 ? 'text-emerald-600' : 'text-amber-600'}`}>{score.readability}%</span>
                </div>
                <div className="flex justify-between text-[11px]">
                  <span className="text-slate-500">Keyword Coverage</span>
                  <span className={`font-mono font-medium ${score.keyword_density >= 30 ? 'text-emerald-600' : 'text-amber-600'}`}>{score.keyword_density}%</span>
                </div>
              </div>
            </div>
            {/* SEO Checks */}
            <div className="space-y-1">
              {score.seo_checks?.map((c, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px]">
                  {c.pass ? <Check className="w-3 h-3 text-emerald-500" /> : <AlertTriangle className="w-3 h-3 text-amber-500" />}
                  <span className={c.pass ? 'text-slate-600' : 'text-amber-700'}>{c.check}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-xs text-slate-400 text-center py-4">Click Analyze to score your content</p>
        )}
      </div>

      {/* AI Generate Full Content */}
      <div className="bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200/60 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-violet-600 flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">AI Content Writer</p>
            <p className="text-[10px] text-slate-500">GPT-5.2 powered</p>
          </div>
        </div>
        <Textarea
          placeholder="Describe the page you want to create... e.g. 'A comprehensive guide comparing Burlington vs Hamilton for families with kids under 10, focused on schools, safety, and pricing'"
          className="text-sm rounded-lg min-h-[80px] mb-3 bg-white border-violet-200"
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          data-testid="ai-prompt"
        />
        <div className="flex items-center gap-2 mb-3">
          <Select value={tone} onValueChange={setTone}>
            <SelectTrigger className="w-[140px] h-8 text-xs rounded-lg bg-white border-violet-200"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="professional">Professional</SelectItem>
              <SelectItem value="conversational">Conversational</SelectItem>
              <SelectItem value="analytical">Analytical</SelectItem>
              <SelectItem value="friendly">Friendly</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={handleGenerate} disabled={generating || !prompt.trim()} className="w-full bg-violet-600 text-white hover:bg-violet-700 rounded-lg h-9 text-xs font-medium gap-1.5" data-testid="ai-generate-btn">
          {generating ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating...</> : <><Wand2 className="w-3.5 h-3.5" /> Generate Content</>}
        </Button>

        {/* Generated result */}
        {result && !result.raw && (
          <div className="mt-4 bg-white border border-violet-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-violet-700">Generated Content</p>
              <span className="text-[10px] text-slate-400">{result.sections?.length || 0} sections</span>
            </div>
            {result.title && <p className="text-sm font-medium text-slate-900 mb-1">{result.title}</p>}
            {result.subtitle && <p className="text-xs text-slate-500 mb-2">{result.subtitle}</p>}
            {result.sections && (
              <div className="space-y-1 mb-3 max-h-40 overflow-y-auto">
                {result.sections.map((s, i) => (
                  <p key={i} className="text-[11px] text-slate-600 truncate">
                    <span className="font-mono text-slate-400">{s.type}</span> — {s.heading || s.button || '(content)'}
                  </p>
                ))}
              </div>
            )}
            <Button onClick={applyGenerated} className="w-full bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg h-8 text-xs font-medium gap-1.5" data-testid="ai-apply-btn">
              <Check className="w-3 h-3" /> Apply to Page
            </Button>
          </div>
        )}
        {result?.raw && (
          <div className="mt-4 bg-white border border-amber-200 rounded-lg p-3">
            <p className="text-xs text-amber-700 mb-2">Could not parse structured content. Raw output:</p>
            <p className="text-[11px] text-slate-600 max-h-32 overflow-y-auto whitespace-pre-wrap">{result.raw.substring(0, 500)}...</p>
          </div>
        )}
      </div>

      {/* Quick Suggestions */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <RefreshCw className="w-4 h-4 text-slate-400" />
          <p className="text-sm font-semibold text-slate-800">Quick Suggestions</p>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <Select value={sugType} onValueChange={setSugType}>
            <SelectTrigger className="flex-1 h-8 text-xs rounded-lg"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="titles">Page Titles</SelectItem>
              <SelectItem value="meta">Meta Descriptions</SelectItem>
              <SelectItem value="sections">Section Ideas</SelectItem>
              <SelectItem value="ctas">CTA Variations</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleSuggestions} disabled={sugLoading} variant="outline" size="sm" className="rounded-lg h-8 text-xs gap-1">
            {sugLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Generate
          </Button>
        </div>
        {suggestions.length > 0 && (
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {suggestions.map((s, i) => (
              <button
                key={i}
                onClick={() => {
                  const text = typeof s === 'string' ? s : JSON.stringify(s);
                  navigator.clipboard.writeText(text);
                }}
                className="w-full text-left p-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors group"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[12px] text-slate-700 leading-relaxed">
                    {typeof s === 'string' ? s : `${s.heading} — ${s.sub}`}
                  </p>
                  <Copy className="w-3 h-3 text-slate-300 group-hover:text-slate-500 flex-shrink-0 mt-0.5" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Editor ─────────────────────────────────────────────
export default function AdminGuideEditor() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isNew = !slug;
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [form, setForm] = useState({
    slug: '', category: '', category_slug: '', title: '', subtitle: '', meta: '',
    sections: [], related_pages: [], related_neighbourhoods: [], status: 'draft',
  });
  const [dupes, setDupes] = useState(null);
  const [linkSuggestions, setLinkSuggestions] = useState(null);
  const [linksLoading, setLinksLoading] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) { navigate('/ops/login'); return; }
    adminGetCategories().then(r => setCategories(r.data.categories || [])).catch(() => {});
    if (!isNew) {
      adminGetGuide(slug).then(r => setForm(r.data)).catch(() => navigate('/ops/guides')).finally(() => setLoading(false));
    }
  }, [slug, isNew, navigate]);

  const updateField = (key, value) => {
    setForm(f => ({ ...f, [key]: value }));
    // Check duplicates on slug or title change
    if (key === 'slug' && value.length > 3) {
      aiCheckDuplicates({ slug: value, title: form.title }).then(r => setDupes(r.data)).catch(() => {});
    } else if (key === 'title' && value.length > 5) {
      aiCheckDuplicates({ slug: form.slug, title: value }).then(r => setDupes(r.data)).catch(() => {});
    }
  };

  const fetchLinkSuggestions = async () => {
    setLinksLoading(true);
    try {
      const r = await aiSuggestLinks({ title: form.title, category_slug: form.category_slug, sections: form.sections });
      setLinkSuggestions(r.data);
    } catch { }
    setLinksLoading(false);
  };
  const handleSectionChange = (index, updated) => { const next = [...form.sections]; next[index] = updated; updateField('sections', next); };
  const addSection = (type) => updateField('sections', [...form.sections, { type }]);
  const removeSection = (index) => updateField('sections', form.sections.filter((_, i) => i !== index));

  const handleSave = async () => {
    if (!form.slug || !form.title || !form.category_slug) { alert('Slug, title, and category are required.'); return; }
    setSaving(true);
    try {
      const cat = categories.find(c => c.slug === form.category_slug);
      const payload = { ...form, category: cat?.name || form.category };
      if (isNew) await adminCreateGuide(payload);
      else await adminUpdateGuide(slug, payload);
      navigate('/ops/guides');
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
    setSaving(false);
  };

  const handleAIApply = (content) => {
    if (content.title) updateField('title', content.title);
    if (content.subtitle) updateField('subtitle', content.subtitle);
    if (content.meta) updateField('meta', content.meta);
    if (content.slug) updateField('slug', content.slug);
    if (content.sections) updateField('sections', content.sections);
    if (content.related_pages) updateField('related_pages', content.related_pages);
    if (content.related_neighbourhoods) updateField('related_neighbourhoods', content.related_neighbourhoods);
    // AI content always starts as draft
    setForm(f => ({ ...f, status: 'draft' }));
  };

  if (loading) return <OpsLayout><div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></OpsLayout>;

  return (
    <OpsLayout>
    <div className="min-h-screen bg-slate-50" data-testid="admin-guide-editor">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 md:px-10 py-4 sticky top-0 z-20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/ops/guides" className="text-sm text-slate-400 hover:text-slate-900 transition-colors flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Pages
            </Link>
            <span className="text-slate-300">|</span>
            <span className="text-sm font-medium text-slate-900">{isNew ? 'New Page' : 'Edit Page'}</span>
          </div>
          <div className="flex items-center gap-3">
            <Select value={form.status} onValueChange={(v) => {
              if (v === 'published') {
                if (!window.confirm('Publish this page? It will be immediately visible to the public.')) return;
              }
              updateField('status', v);
            }}>
              <SelectTrigger className="w-[120px] h-8 text-xs rounded-lg" data-testid="status-toggle"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleSave} disabled={saving} className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg h-9 px-5 text-xs font-medium gap-1.5" data-testid="save-guide-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {isNew ? 'Create' : 'Save'}
            </Button>
          </div>
        </div>
      </div>

      <div className="px-6 md:px-10 py-6">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* Main editor */}
          <div className="xl:col-span-7 space-y-5">
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
              <div>
                <Label className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold mb-1.5 block">Page Title *</Label>
                <Input className="text-sm rounded-lg h-10" placeholder="Burlington vs. Oakville" value={form.title} onChange={e => updateField('title', e.target.value)} data-testid="input-title" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold mb-1.5 block">URL Slug *</Label>
                <div className="flex items-center">
                  <span className="text-xs text-slate-400 bg-slate-50 border border-r-0 border-slate-200 rounded-l-lg px-3 h-10 flex items-center">/guides/</span>
                  <Input className="text-sm rounded-l-none rounded-r-lg h-10 font-mono" placeholder="burlington-vs-oakville" value={form.slug} onChange={e => updateField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} data-testid="input-slug" />
                </div>
                {/* Duplicate warnings */}
                {dupes?.slug_exact && !slug && (
                  <div className="flex items-center gap-2 mt-1.5 text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    <p className="text-[11px]">Slug <span className="font-mono font-medium">{dupes.slug_exact.slug}</span> already exists: "{dupes.slug_exact.title}"</p>
                  </div>
                )}
                {dupes?.slug_similar?.length > 0 && (
                  <div className="mt-1.5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <p className="text-[11px] text-amber-700 font-medium mb-1">Similar pages exist:</p>
                    {dupes.slug_similar.slice(0, 3).map((d, i) => (
                      <p key={i} className="text-[10px] text-amber-600 font-mono">/{d.slug} — {d.title}</p>
                    ))}
                  </div>
                )}
                {dupes?.title_similar?.length > 0 && !dupes?.slug_exact && (
                  <div className="mt-1.5 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                    <p className="text-[11px] text-blue-700 font-medium mb-1">Similar titles found:</p>
                    {dupes.title_similar.slice(0, 3).map((d, i) => (
                      <p key={i} className="text-[10px] text-blue-600">"{d.title}" <span className="font-mono text-blue-400">/{d.slug}</span></p>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold mb-1.5 block">Subtitle</Label>
                <Textarea className="text-sm rounded-lg min-h-[60px]" placeholder="Compelling subtitle..." value={form.subtitle} onChange={e => updateField('subtitle', e.target.value)} />
              </div>
              <div>
                <Label className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold mb-1.5 block">Meta Description (SEO)</Label>
                <Textarea className="text-sm rounded-lg min-h-[60px]" placeholder="150-160 chars..." value={form.meta} onChange={e => updateField('meta', e.target.value)} />
                <p className="text-[10px] text-slate-400 mt-1">{(form.meta || '').length}/160</p>
              </div>
            </div>

            {/* Sections */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-slate-800">Content Sections</h2>
                <span className="font-mono text-xs text-slate-400">{form.sections.length} sections</span>
              </div>
              <div className="space-y-3">
                {form.sections.map((section, i) => (
                  <SectionEditor key={i} section={section} index={i} onChange={handleSectionChange} onRemove={removeSection} pageTitle={form.title} />
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {SECTION_TYPES.map(t => (
                  <Button key={t.value} variant="outline" size="sm" className="rounded-lg h-8 text-xs gap-1.5" onClick={() => addSection(t.value)}>
                    <Plus className="w-3 h-3" /> {t.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Right sidebar — AI + Settings */}
          <div className="xl:col-span-5 space-y-5">
            {/* AI Assistant */}
            <AIAssistant form={form} categories={categories} onApply={handleAIApply} />

            {/* Category */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <Label className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold mb-2 block">Category (Silo) *</Label>
              <Select value={form.category_slug || ""} onValueChange={(v) => { const cat = categories.find(c => c.slug === v); updateField('category_slug', v); updateField('category', cat?.name || v); }}>
                <SelectTrigger className="w-full h-9 text-sm rounded-lg"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* Internal Linking — AI-powered */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-slate-400" />
                  <Label className="text-sm font-semibold text-slate-800">Internal Links</Label>
                </div>
                <Button onClick={fetchLinkSuggestions} disabled={linksLoading} variant="ghost" size="sm" className="h-7 text-xs gap-1 text-violet-600 hover:bg-violet-50">
                  {linksLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Suggest
                </Button>
              </div>

              {/* Current selections */}
              <div className="mb-3">
                <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold mb-1.5">Related Guides</p>
                <Textarea className="text-xs rounded-lg min-h-[50px] font-mono" placeholder="one-slug-per-line" value={(form.related_pages || []).join('\n')} onChange={e => updateField('related_pages', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))} />
              </div>
              <div className="mb-3">
                <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold mb-1.5">Related Neighbourhoods</p>
                <Textarea className="text-xs rounded-lg min-h-[50px] font-mono" placeholder="alton-village&#10;roseland" value={(form.related_neighbourhoods || []).join('\n')} onChange={e => updateField('related_neighbourhoods', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))} />
              </div>

              {/* AI Suggestions */}
              {linkSuggestions && (
                <div className="border-t border-slate-100 pt-3 mt-3">
                  <p className="text-[10px] text-violet-600 font-semibold uppercase tracking-widest mb-2">AI Suggestions</p>
                  {linkSuggestions.related_neighbourhoods?.length > 0 && (
                    <div className="mb-2">
                      <p className="text-[10px] text-slate-500 mb-1">Neighbourhoods mentioned:</p>
                      <div className="flex flex-wrap gap-1">
                        {linkSuggestions.related_neighbourhoods.map((s, i) => (
                          <button key={i} onClick={() => {
                            const current = form.related_neighbourhoods || [];
                            if (!current.includes(s.slug)) updateField('related_neighbourhoods', [...current, s.slug]);
                          }} className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                            (form.related_neighbourhoods || []).includes(s.slug)
                              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                              : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-emerald-50 hover:border-emerald-200'
                          }`}>
                            {(form.related_neighbourhoods || []).includes(s.slug) ? <Check className="w-2.5 h-2.5 inline mr-0.5" /> : <Plus className="w-2.5 h-2.5 inline mr-0.5" />}
                            {s.title}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {linkSuggestions.related_pages?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-slate-500 mb-1">Related guides:</p>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {linkSuggestions.related_pages.map((s, i) => (
                          <button key={i} onClick={() => {
                            const current = form.related_pages || [];
                            if (!current.includes(s.slug)) updateField('related_pages', [...current, s.slug]);
                          }} className={`w-full text-left flex items-center justify-between px-2 py-1.5 rounded-lg text-[11px] transition-colors ${
                            (form.related_pages || []).includes(s.slug)
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-slate-50 text-slate-600 hover:bg-emerald-50'
                          }`}>
                            <span className="truncate">{s.title}</span>
                            <span className="text-[9px] text-slate-400 flex-shrink-0 ml-2">{s.relevance}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {form.slug && (
              <Link to={`/guides/${form.slug}`} target="_blank" className="block text-center text-xs text-emerald-600 hover:text-emerald-700">
                Preview: /guides/{form.slug} &rarr;
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
    </OpsLayout>
  );
}
